import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IQuickValidationState } from './types';
import {
  BookingStatusNames,
  bookingStatusType as BookingStatusType,
  IBooking,
  IBookingStatus,
  BookingMethods,
  preAuthStatus,
  servicePaymentType,
  BookingType, bookingStatusType,
} from 'shared-types/index';
import { DiaryRootState } from 'appRoot/rootReducer';
import {
  BookingCreatorPanelType,
  BookingValidationItems,
  IValidationErrorListItem,
} from 'external-services/booking-validation-service/bookingValidation.types';
import BookingValidationService from 'external-services/booking-validation-service';
import { SaveStatus } from 'booking-creator/reducers/validation/types';
import { cloneDeep } from 'lodash-es';
import { bookingActions } from 'app/reducers/booking/slice';
import BookingUpdateService from 'external-services/booking-update-service';
import { errorStateActions, getAccordionDetail } from 'app/reducers/errorState/slice';
import { CONTACT_MSG, PROBLEMS_MSG, REFRESH_MSG } from 'appRoot/app-error-messages';
import { scrollPositionActions } from 'booking-creator/reducers/scrollPosition/slice';
import { paymentActions } from 'booking-creator/reducers/payment/slice';
import { displayActions } from 'booking-creator/reducers/display/slice';
import DateUtilsService from 'external-services/date-utils-service';
import BookingLookupService from 'external-services/booking-lookup-service';
const NS = 'quickValidationSlice';

const initialState: IQuickValidationState = {
  canSave: true,
  cantSaveMessageList: [],
  staffValue: '',
  originStatus: null,
  newStatus: null,
  isIgnoreStaff: false,
  quickSaveAttempted: false,
  isCancelClicked: false,
  canSaveQuickCancelForSBL: false,
  originPax: 0,
  newPax: 0,
};

export const checkQuickEditBooking = (
  state: DiaryRootState,
  oldStaffCheckType: 'generalSettings' | 'validationReducer'
) => {
  const { staffValidationReducer, settingReducer, validationReducer } = state;

  if (staffValidationReducer?.hasOwnProperty('staffEdit')) {
    if (staffValidationReducer?.staffEditEnabled && staffValidationReducer?.staffEditRequired) {
      return Boolean(staffValidationReducer.staffEdit['staffEditQuickEdit']);
    } else {
      return false;
    }
  }
  if (oldStaffCheckType === 'generalSettings') {
    return (
      settingReducer.generalSettings.staffEditEnabled &&
      settingReducer.generalSettings.staffEditRequired
    );
  }
  if (oldStaffCheckType === 'validationReducer') {
    return (
      validationReducer.staffEditBooking.isEnabled && validationReducer.staffEditBooking.isMandatory
    );
  }
  return false;
};

export const updateCanSaveStatusChange = createAsyncThunk(
  'updateCanSaveStatusChange/quickValidation',
  async (
    payload,
    { getState }
  ): Promise<{ canSave: boolean; cantSaveMessageList: IValidationErrorListItem[] }> => {
    const state: DiaryRootState = getState() as DiaryRootState;
    const quickValidationReducer = state.quickValidationReducer;
    const { staffValue, originStatus, newStatus, isIgnoreStaff, newPax, originPax } =
      quickValidationReducer;
    const isStaffEditQuickEdit = checkQuickEditBooking(state, 'generalSettings');

    const validationList: BookingValidationItems[] = [];

    let canSave = true;
    let cantSaveMessageList: IValidationErrorListItem[] = [];

    if (
      !!originStatus &&
      !!newStatus &&
      originStatus.name === newStatus.name &&
      newPax === originPax
    ) {
      return { canSave, cantSaveMessageList };
    }

    if (
      !!originStatus &&
      !!newStatus &&
      originStatus.name !== newStatus.name &&
      newPax === originPax &&
      newStatus.name === BookingStatusNames.Seated
    ) {
      return { canSave, cantSaveMessageList };
    }

    if (
      !staffValue &&
      !isIgnoreStaff && // ignore if walkin
      isStaffEditQuickEdit
    ) {
      canSave = false;
      validationList.unshift(BookingValidationItems.staffName);
      return {
        canSave,
        cantSaveMessageList: BookingValidationService.getValidationMessages(validationList),
      };
    }

    // if (!!originStatus && !!newStatus && originStatus.name === newStatus.name) {
    //   canSave = false;
    //   validationList.unshift(BookingValidationItems.notChangedStatus);
    // }

    cantSaveMessageList = BookingValidationService.getValidationMessages(validationList);

    return { canSave, cantSaveMessageList };
  }
);

/**
 * Only use when change speical properties such as status (not for newBooking or editBooking)
 * Currently support for only status change.
 */

export const saveQuickEditBooking = createAsyncThunk(
  'validation/saveQuickEditBooking',
  async (
    payload: {
      name?: string;
      isManualUpdate: boolean;
      booking: Partial<IBooking>;
      key?: keyof Partial<IBooking>;
      value?: any;
    },
    { dispatch, getState }
  ): Promise<SaveStatus> => {
    const state = getState() as DiaryRootState;

    const { statusReducer, settingReducer, quickValidationReducer, venueReducer } = state;
    const bookingDate = DateUtilsService.getDateFromBookingId(payload.booking._id);
    const bookingToUpdate = await BookingLookupService.getById(payload.booking._id, bookingDate);

    const { generalSettings } = settingReducer;
    const { staffValue, isIgnoreStaff } = quickValidationReducer;

    const isStaffEditRequired = checkQuickEditBooking(state, 'generalSettings');

    // NBI-8715 - if walkin and quick edit then no validation needed - isIgnoreStaff is true and below validation should be ignored.
    if (!isIgnoreStaff && isStaffEditRequired && !staffValue && payload.name !== BookingStatusNames.Seated) {
      return SaveStatus.ValidationFailed;
    }

    const staffInfo: string =
      generalSettings?.staffEditEnabled && !isIgnoreStaff ? staffValue : null;

    const isManualUpdate = payload.isManualUpdate;

    let newBooking = cloneDeep(bookingToUpdate);
    // for status update
    if (payload.name) {
      const status = statusReducer.fullStatusOptions.find((s) => s.name === payload.name);
      // trigger pre-auth dialog when quick edit status to cancelled and payment is pre-auth and pre-auth status is holding
      if (
        status.statusType === BookingStatusType.cancelled &&
        newBooking.paymentDetails?.paymentType === servicePaymentType.preAuth &&
        newBooking?.paymentSummary?.preAuthStatus === preAuthStatus.holding
      ) {
        dispatch(displayActions.setActiveBooking(newBooking as IBooking)); // set activeBooking to show in pre-auth dialog, will reset when close dialog
        dispatch(paymentActions.setPreAuthDialog(true));
      } else {
        dispatch(paymentActions.setPreAuthDialog(false));
      }

      newBooking = {
        ...newBooking,
        status,
      };
      // finished status need change endTime to currentTime
      if (status.statusType === bookingStatusType.finished) {
        const timeZoneId = venueReducer.timeZoneId;
        const currentTime = DateUtilsService.getVenueTime(timeZoneId)
        if (newBooking.bookingEnd > currentTime && newBooking.time < currentTime) {
          newBooking.bookingEnd = currentTime;
          newBooking.duration = Math.floor(DateUtilsService.minsBetweenDates(newBooking.time, currentTime));
        }
      } else if (status.statusType === bookingStatusType.seated) {
        /**
         * NBI-9190: seated status within half an hour of original start time, gets
         * adjusted to the current now time.
         */
        BookingUpdateService.updateTimesOnBookingWhenSeated(newBooking, bookingToUpdate.time);
      }
    }
    // for other update
    if (!!payload.key && !!payload.value) {
      newBooking = {
        ...newBooking,
        [payload.key]: payload.value,
      };
    }
    
    try {
      const updatedBooking = await BookingUpdateService.updateFromAnywhere(
        newBooking as IBooking,
        bookingToUpdate as IBooking,
        null,
        null,
        staffInfo
      );
      // Re-read state after the async operation, because the active booking may have changed
      // while updateFromAnywhere was in-flight (e.g. staff opened a different booking).
      // If they no longer match, do NOT overwrite originalBooking — that is the root cause of
      // the bug where a new booking is created instead of updating the existing one.
      const currentActiveBookingId = (getState() as DiaryRootState).displayReducer.activeBooking?._id;
      if (currentActiveBookingId === payload.booking._id) {
        dispatch(displayActions.setBackUpActiveBooking(cloneDeep(updatedBooking)));
        dispatch(displayActions.forceSetOriginalBooking(updatedBooking as IBooking));
      } else {
        console.warn(
          NS,
          'saveQuickEditBooking: active booking changed during async save — skipping forceSetOriginalBooking',
          { savedBookingId: payload.booking._id, currentActiveBookingId }
        );
      }
      if (isManualUpdate) {
        dispatch(bookingActions.setRecentChangeBookingIdToIgnore(newBooking._id));
      }
      return SaveStatus.SaveSuccessful;
    } catch (error) {
      dispatch(
        errorStateActions.setErrorState({
          errorTitle: 'Problem saving changes',
          errorMessage: `There was an error trying to save your changes to the status. \n\n${PROBLEMS_MSG} ${REFRESH_MSG} \n\n${CONTACT_MSG}`,
          accordionDetail: getAccordionDetail(error),
        })
      );
      return SaveStatus.SaveFailed;
    }
  }
);

export const updateCanSaveCancelSBLBooking = createAsyncThunk(
  'validation/updateCanSaveCancelSBLBooking',
  async (payload: void, { dispatch, getState }): Promise<{ canSave: boolean }> => {
    const state: DiaryRootState = getState() as DiaryRootState;
    const { quickValidationReducer } = state;
    const isStaffEditRequired = checkQuickEditBooking(state, 'validationReducer');
    const { isCancelClicked, staffValue, isIgnoreStaff } = quickValidationReducer;

    const canSave = isStaffEditRequired ? isCancelClicked && Boolean(staffValue) : true;

    const panels: BookingCreatorPanelType[] = canSave ? [] : [BookingCreatorPanelType.staff];
    dispatch(scrollPositionActions.setValidationErrorPanels(panels));

    return { canSave };
  }
);

const quickValidationSlice = createSlice({
  name: 'quickValidation', // used in action types
  initialState,
  reducers: {
    setStaffValue: (state: IQuickValidationState, action: PayloadAction<string>) => {
      state.staffValue = action.payload;
    },
    clearValidationAndSetBothStatuses: (
      state: IQuickValidationState,
      action: PayloadAction<{
        status?: IBookingStatus;
        isIgnoreStaff: boolean;
      }>
    ) => {
      state.canSave = true;
      state.cantSaveMessageList = [];
      state.staffValue = '';
      state.originStatus = action.payload?.status;
      state.newStatus = action.payload?.status;
      state.isIgnoreStaff = action.payload?.isIgnoreStaff;
      state.quickSaveAttempted = false;
      state.newPax = 0;
      state.originPax = 0;
      state.isCancelClicked = false;
      state.canSaveQuickCancelForSBL = false;
    },
    setOriginPax: (state: IQuickValidationState, action: PayloadAction<number>) => {
      state.originPax = action.payload;
      state.newPax = action.payload;
    },
    setNewPax: (state: IQuickValidationState, action: PayloadAction<number>) => {
      state.newPax = action.payload;
    },
    setNewStatus: (state: IQuickValidationState, action: PayloadAction<IBookingStatus>) => {
      state.newStatus = action.payload;
    },
    setQuickSaveAttempted: (state: IQuickValidationState, action: PayloadAction<boolean>) => {
      state.quickSaveAttempted = action.payload;
    },
    setIsCancelClicked: (state: IQuickValidationState, action: PayloadAction<boolean>) => {
      state.isCancelClicked = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateCanSaveStatusChange.fulfilled, (state: IQuickValidationState, action) => {
      state.canSave = action.payload.canSave;
      state.cantSaveMessageList = action.payload.cantSaveMessageList;
    });
    builder.addCase(saveQuickEditBooking.fulfilled, (state: IQuickValidationState, action) => {
      state.quickSaveAttempted = true;
    });
    builder.addCase(
      updateCanSaveCancelSBLBooking.fulfilled,
      (state: IQuickValidationState, action) => {
        state.canSaveQuickCancelForSBL = action.payload.canSave;
      }
    );
  },
});

// Extract the action creators object and the reducer
const { actions, reducer } = quickValidationSlice;
// Extract and export each action creator by name
export { actions as quickValidationActions };
// Export the reducer, either as a default or named export
export default reducer;
